import React, { createContext, useContext, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import { ThemeProvider } from './components/ThemeProvider';

// Pages
import LandingPage from './pages/LandingPage';
import AuthPage from './pages/AuthPage';
import ProfileSetup from './pages/ProfileSetup';
import Onboarding from './pages/Onboarding';
import Discovery from './pages/Discovery';
import Matches from './pages/Matches';
import Messages from './pages/Messages';
import Chat from './pages/Chat';
import Settings from './pages/Settings';
import Premium from './pages/Premium';
import PaymentSuccess from './pages/PaymentSuccess';
import BoostSuccess from './pages/BoostSuccess';
import TrialSuccess from './pages/TrialSuccess';
import ProfileView from './pages/ProfileView';
import ForgotPassword from './pages/ForgotPassword';
import BlockedUsers from './pages/BlockedUsers';
import VideoCall from './pages/VideoCall';
import WhoLikesMe from './pages/WhoLikesMe';
import ActivityFeed from './pages/ActivityFeed';
import EditPrompts from './pages/EditPrompts';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import AdminUsers from './pages/AdminUsers';
import AdminPayments from './pages/AdminPayments';
import AccountSettings from './pages/AccountSettings';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import SalesTerms from './pages/SalesTerms';
import CampaignPage from './pages/CampaignPage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [appConfig, setAppConfig] = useState({ premium_enabled: true });

  // Fetch app config
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await axios.get(`${API}/app/config`);
        setAppConfig(res.data);
      } catch (err) {
        console.log('Could not fetch app config');
      }
    };
    fetchConfig();
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        try {
          const res = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${storedToken}` }
          });
          setUser(res.data);
          setToken(storedToken);
        } catch (err) {
          localStorage.removeItem('token');
          setToken(null);
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem('token', res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const register = async (email, password, name) => {
    const res = await axios.post(`${API}/auth/register`, { email, password, name });
    localStorage.setItem('token', res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const updateUser = (userData) => {
    setUser(prev => ({ ...prev, ...userData }));
  };

  const refreshUser = async () => {
    if (token) {
      const res = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(res.data);
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, updateUser, refreshUser, appConfig, premiumEnabled: appConfig.premium_enabled }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route - checks for onboarding completion
const ProtectedRoute = ({ children, skipOnboarding = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-primary text-xl font-display">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // Check if onboarding is needed (skip for onboarding page itself and settings)
  if (!skipOnboarding && !user.onboarding_completed) {
    // Check if user has minimum profile data
    const hasMinimumProfile = 
      user.photos?.length > 0 && 
      user.bio && 
      user.gender && 
      user.interests?.length >= 3;
    
    if (!hasMinimumProfile) {
      return <Navigate to="/onboarding" replace />;
    }
  }

  return children;
};

// Main App Routes
const AppRoutes = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-primary text-xl font-display">Loading...</div>
      </div>
    );
  }

  // Redirect to onboarding if not completed
  const getDefaultRedirect = () => {
    if (!user) return '/';
    if (!user.onboarding_completed) {
      const hasMinimumProfile = 
        user.photos?.length > 0 && 
        user.bio && 
        user.gender && 
        user.interests?.length >= 3;
      if (!hasMinimumProfile) return '/onboarding';
    }
    return '/discover';
  };

  return (
    <Routes>
      <Route path="/" element={user ? <Navigate to={getDefaultRedirect()} /> : <LandingPage />} />
      <Route path="/auth" element={user ? <Navigate to={getDefaultRedirect()} /> : <AuthPage />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/onboarding" element={<ProtectedRoute skipOnboarding={true}><Onboarding /></ProtectedRoute>} />
      <Route path="/setup" element={<ProtectedRoute skipOnboarding={true}><ProfileSetup /></ProtectedRoute>} />
      <Route path="/discover" element={<ProtectedRoute><Discovery /></ProtectedRoute>} />
      <Route path="/matches" element={<ProtectedRoute><Matches /></ProtectedRoute>} />
      <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
      <Route path="/chat/:userId" element={<ProtectedRoute><Chat /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute skipOnboarding={true}><Settings /></ProtectedRoute>} />
      <Route path="/premium" element={<ProtectedRoute><Premium /></ProtectedRoute>} />
      <Route path="/payment-success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
      <Route path="/boost-success" element={<ProtectedRoute><BoostSuccess /></ProtectedRoute>} />
      <Route path="/trial-success" element={<ProtectedRoute><TrialSuccess /></ProtectedRoute>} />
      <Route path="/profile/:userId" element={<ProtectedRoute><ProfileView /></ProtectedRoute>} />
      <Route path="/blocked-users" element={<ProtectedRoute><BlockedUsers /></ProtectedRoute>} />
      <Route path="/video-call/:matchId" element={<ProtectedRoute><VideoCall /></ProtectedRoute>} />
      <Route path="/likes" element={<ProtectedRoute><WhoLikesMe /></ProtectedRoute>} />
      <Route path="/activity" element={<ProtectedRoute><ActivityFeed /></ProtectedRoute>} />
      <Route path="/edit-prompts" element={<ProtectedRoute><EditPrompts /></ProtectedRoute>} />
      <Route path="/account" element={<ProtectedRoute><AccountSettings /></ProtectedRoute>} />
      
      {/* Admin Routes */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="/admin/users" element={<AdminUsers />} />
      <Route path="/admin/payments" element={<AdminPayments />} />
      
      {/* Legal Pages */}
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/sales-terms" element={<SalesTerms />} />
      
      {/* Campaign Landing Page for Ads */}
      <Route path="/lp" element={<CampaignPage />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <div className="min-h-screen bg-background">
            <AppRoutes />
            <Toaster position="top-center" richColors />
          </div>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
